import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem("admin_token", res.data.access_token);
      localStorage.setItem("admin_user", JSON.stringify(res.data.admin));
      localStorage.setItem("admin_info", JSON.stringify(res.data.admin));
      toast.success(`Welcome back, ${res.data.admin.name}!`);
      // Route by role: contractors -> Talent Partner Portal, admin/super_admin -> Admin Dashboard
      if (res.data.admin.role === "contractor") {
        navigate("/portal");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid username or password");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    try {
      const res = await axios.post(`${API}/admin/setup`);
      if (res.data.created?.length > 0) {
        toast.success(`Accounts created: ${res.data.created.join(", ")}`);
      } else {
        toast.info("Accounts already exist. Please login.");
      }
    } catch (error) {
      toast.error("Setup failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A]">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B8860B]/10 rounded-full mb-6">
            <Lock size={28} className="text-[#B8860B]" />
          </div>
          <h1 className="font-display text-3xl text-white mb-2">Admin Login</h1>
          <p className="text-gray-500 text-sm">Magnus Kor Talent Partners Dashboard</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="text-sm text-gray-400 block mb-2">Username</label>
            <input
              type="text"
              required
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full bg-gray-900 border border-gray-800 text-white px-4 py-3 focus:border-[#B8860B] focus:outline-none transition-colors"
              placeholder="Enter username"
              data-testid="admin-username"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 block mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full bg-gray-900 border border-gray-800 text-white px-4 py-3 pr-12 focus:border-[#B8860B] focus:outline-none transition-colors"
                placeholder="Enter password"
                data-testid="admin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#B8860B] transition-colors"
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#B8860B] text-white py-3 font-medium hover:bg-[#9A7209] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-gray-500">
          <span>Are you a Talent Partner? </span>
          <button
            onClick={() => navigate("/portal")}
            className="text-[#B8860B] hover:underline"
            data-testid="admin-login-portal-switch"
          >
            Sign in to your portal →
          </button>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-xs mb-4">First time? Set up admin account:</p>
          <button
            onClick={handleSetup}
            className="text-[#B8860B] text-sm hover:underline"
          >
            Create Default Admin
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
